/**
 * Lazy Loading Utility
 * 
 * Provides utilities for lazy loading heavy dependencies and components
 */

/**
 * Lazy load a script from CDN or local source
 * @param {string} src Script source URL
 * @param {string} globalVar Global variable to check for existence
 * @returns {Promise} Promise that resolves when script is loaded
 */
export function loadScript(src, globalVar) {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (globalVar && window[globalVar]) {
      resolve(window[globalVar]);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector(`script[src="${src}"]`);
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window[globalVar]));
      existingScript.addEventListener("error", reject);
      return;
    }

    // Create and load script
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    
    script.onload = () => resolve(window[globalVar]);
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    
    document.head.appendChild(script);
  });
}

/**
 * Lazy load CSS file
 * @param {string} href CSS file URL
 * @returns {Promise} Promise that resolves when CSS is loaded
 */
export function loadCSS(href) {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    const existingLink = document.querySelector(`link[href="${href}"]`);
    if (existingLink) {
      resolve();
      return;
    }

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    
    link.onload = () => resolve();
    link.onerror = () => reject(new Error(`Failed to load CSS: ${href}`));
    
    document.head.appendChild(link);
  });
}

/**
 * Lazy load ECharts library using dynamic imports
 * @returns {Promise} Promise that resolves with echarts instance
 */
export async function loadECharts() {
  try {
    // Check if already loaded
    if (window.echarts) {
      return window.echarts;
    }

    // Dynamically import ECharts
    const echartsModule = await import("echarts");
    const echarts = echartsModule.default || echartsModule;
    
    // Make it available globally for compatibility
    window.echarts = echarts;
    
    return echarts;
  } catch (error) {
    console.error("Failed to load ECharts:", error);
    throw error;
  }
}

/**
 * Lazy load Mapbox GL JS from CDN
 * @returns {Promise} Promise that resolves with mapboxgl instance
 */
export async function loadMapboxGL() {
  await loadCSS("https://api.mapbox.com/mapbox-gl-js/v3.12.0/mapbox-gl.css");
  return loadScript(
    "https://api.mapbox.com/mapbox-gl-js/v3.12.0/mapbox-gl.js",
    "mapboxgl"
  );
}

/**
 * Intersection Observer for lazy loading components when they come into view
 * @param {Element} element Element to observe
 * @param {Function} callback Function to call when element is visible
 * @param {Object} options IntersectionObserver options
 */
export function observeVisibility(element, callback, options = {}) {
  const defaultOptions = {
    root: null,
    rootMargin: "50px",
    threshold: 0.1,
    ...options
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        callback(entry.target);
        observer.unobserve(entry.target);
      }
    });
  }, defaultOptions);

  observer.observe(element);
  return observer;
}

/**
 * Debounce function for performance optimization
 * @param {Function} func Function to debounce
 * @param {number} wait Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle function for performance optimization
 * @param {Function} func Function to throttle
 * @param {number} limit Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export function throttle(func, limit) {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}